import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { buscarComboPorCodigo } from "../../services/auth.service";
import { buscarContasSpedListaPorCnpj, processarRelatorio } from "../../services/api-relatorios.service";
import MensagemErro from "../MensagemErro";
import BlueButton from "../BlueButton";
import {
  canonicalFilaCsvToken,
  sortFiltrosFilaCsv,
  tokenFieldKind,
} from "../../utils/filaCsvTokens.util";
import { isRelatorioConciliacaoSped } from "../../utils/relatorioConciliacaoSped.util";

const FORMATO_DATA = /^\d{4}-\d{2}-\d{2}$/;
const CNPJ_DIGITOS = /^\d{14}$/;
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SX_SWITCH = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#18316E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#18316E",
  },
};

async function carregarCombo(codCombo) {
  const response = await buscarComboPorCodigo(codCombo);
  const combosFormat = (response.detalhes.itensCombo || [])
    .filter((item) => item)
    .map((item) => {
      const split = item.split("/|/");
      return { id: split[0], detalhes: split[1] };
    });
  return combosFormat.filter(
    (combo, index, self) =>
      index === self.findIndex((t) => t.detalhes === combo.detalhes)
  );
}

function LinhaSwitchBinario({
  labelEsquerda,
  labelDireita,
  checked,
  onChange,
  ariaLabel,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flexWrap: "wrap",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: !checked ? "primary.main" : "text.secondary",
          fontWeight: !checked ? 600 : 400,
        }}
      >
        {labelEsquerda}
      </Typography>
      <Switch
        size="small"
        checked={checked}
        onChange={onChange}
        inputProps={{ "aria-label": ariaLabel }}
        sx={{ ...SX_SWITCH, flexShrink: 0 }}
      />
      <Typography
        variant="body2"
        sx={{
          color: checked ? "primary.main" : "text.secondary",
          fontWeight: checked ? 600 : 400,
        }}
      >
        {labelDireita}
      </Typography>
    </Box>
  );
}

/**
 * Modal única: relatórios com `processaFilaCsv` — campos a partir de `filtros[].variavel` (opção A) + grupo/empresa.
 */
export default function ModalFilaCsvDinamica({ relatorio, onClose, onFinished }) {
  const idRelatorio = Number(relatorio?.idRelatorio);
  const filtros = relatorio?.filtros ?? [];

  const [grupos, setGrupos] = useState([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState(null);
  const [listaEmpresas, setListaEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [empresasSelecionadas, setEmpresasSelecionadas] = useState([]);
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [opcoesContas, setOpcoesContas] = useState([]);
  const [contasSelecionadas, setContasSelecionadas] = useState([]);
  const [notasSaida, setNotasSaida] = useState("Cruzadas");
  const [prodId, setProdId] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingContas, setLoadingContas] = useState(false);
  const [snackErro, setSnackErro] = useState({ open: false, msg: "" });

  const filtrosOrdenados = useMemo(() => sortFiltrosFilaCsv(filtros), [filtros]);

  /** Alinhado a `montarPlanosParametroExecucaoV1` (api-relatorio-apuracao): contas obrigatórias por filtro, por `${contas}` no script ou conciliação SPED. */
  const precisaContas = useMemo(() => {
    const scriptStr = String(relatorio?.script ?? "");
    const porFiltros = filtrosOrdenados.some(
      (f) => tokenFieldKind(canonicalFilaCsvToken(f?.variavel)) === "contas"
    );
    const porScript = scriptStr.includes("${contas}");
    const porConciliacao = isRelatorioConciliacaoSped(
      relatorio?.nome,
      relatorio?.codigoRelatorio
    );
    return porFiltros || porScript || porConciliacao;
  }, [
    filtrosOrdenados,
    relatorio?.nome,
    relatorio?.codigoRelatorio,
    relatorio?.script,
  ]);

  /** Extrator multi-CNPJ: só quando flag do catálogo e sem contas/conciliação. */
  const multiCnpj = Boolean(relatorio?.multiCnpj) && !precisaContas;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lista = await carregarCombo("GRUPOECONOMICO");
        if (!cancelled) setGrupos(lista);
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!grupoSelecionado) {
      setListaEmpresas([]);
      setEmpresaSelecionada(null);
      setEmpresasSelecionadas([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const lista = await carregarCombo(
          `EMPRESA_POR_GRUPO_${grupoSelecionado.id}`
        );
        if (!cancelled) {
          setListaEmpresas(lista);
          setEmpresaSelecionada(null);
          setEmpresasSelecionadas([]);
        }
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [grupoSelecionado]);

  useEffect(() => {
    if (!precisaContas) {
      setOpcoesContas([]);
      setContasSelecionadas([]);
      return;
    }
    if (!empresaSelecionada?.id) {
      setOpcoesContas([]);
      setContasSelecionadas([]);
      setLoadingContas(false);
      return;
    }
    const cnpj = String(empresaSelecionada.id).replace(/\D/g, "");
    if (cnpj.length < 11 || cnpj.length > 14) {
      setOpcoesContas([]);
      setContasSelecionadas([]);
      setLoadingContas(false);
      return;
    }
    let cancelled = false;
    setLoadingContas(true);
    (async () => {
      try {
        const res = await buscarContasSpedListaPorCnpj(cnpj);
        const contas = res?.contas ?? [];
        if (!cancelled) {
          setOpcoesContas(contas);
          setContasSelecionadas([]);
        }
      } catch (e) {
        if (!cancelled) {
          setOpcoesContas([]);
          setContasSelecionadas([]);
          setSnackErro({
            open: true,
            msg:
              e?.response?.data?.mensagem ||
              e?.message ||
              "Não foi possível carregar as contas de resultado.",
          });
        }
      } finally {
        if (!cancelled) setLoadingContas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [precisaContas, empresaSelecionada]);

  const filtroDataInicialMeta = filtrosOrdenados.find(
    (f) => tokenFieldKind(canonicalFilaCsvToken(f?.variavel)) === "dataInicial"
  );
  const filtroDataFinalMeta = filtrosOrdenados.find(
    (f) => tokenFieldKind(canonicalFilaCsvToken(f?.variavel)) === "dataFinal"
  );
  const filtroNotasSaidaMeta = filtrosOrdenados.find(
    (f) => tokenFieldKind(canonicalFilaCsvToken(f?.variavel)) === "notasSaida"
  );
  const filtroProdIdMeta = filtrosOrdenados.find(
    (f) => tokenFieldKind(canonicalFilaCsvToken(f?.variavel)) === "prodId"
  );

  const filtrosCamposMeio = useMemo(
    () =>
      filtrosOrdenados.filter((f) => {
        const k = tokenFieldKind(canonicalFilaCsvToken(f?.variavel));
        return ![
          "dataInicial",
          "dataFinal",
          "cnpj",
          "schema",
          "notasSaida",
          "prodId",
        ].includes(k);
      }),
    [filtrosOrdenados]
  );

  const validarComum = () => {
    if (!Number.isFinite(idRelatorio) || idRelatorio <= 0) {
      return "Relatório inválido.";
    }
    if (!grupoSelecionado) return "Selecione o grupo econômico.";
    if (multiCnpj) {
      if (!empresasSelecionadas?.length) {
        return listaEmpresas.length === 0
          ? "Não há empresas disponíveis para o grupo selecionado."
          : "Selecione ao menos uma empresa.";
      }
      for (const emp of empresasSelecionadas) {
        const digitos = String(emp?.id ?? "").replace(/\D/g, "");
        if (!CNPJ_DIGITOS.test(digitos)) {
          return "CNPJ da empresa inválido (14 dígitos).";
        }
      }
    } else {
      if (!empresaSelecionada?.id) {
        return listaEmpresas.length === 0
          ? "Não há empresas disponíveis para o grupo selecionado."
          : "Selecione uma empresa.";
      }
      const cnpj = String(empresaSelecionada.id).replace(/\D/g, "");
      if (!CNPJ_DIGITOS.test(cnpj)) return "CNPJ da empresa inválido (14 dígitos).";
    }
    if (!dataInicial || !FORMATO_DATA.test(dataInicial)) {
      return "Informe a data inicial (YYYY-MM-DD).";
    }
    if (!dataFinal || !FORMATO_DATA.test(dataFinal)) {
      return "Informe a data final (YYYY-MM-DD).";
    }
    if (
      dataInicial &&
      dataFinal &&
      FORMATO_DATA.test(dataInicial) &&
      FORMATO_DATA.test(dataFinal) &&
      dataFinal < dataInicial
    ) {
      return "A data final não pode ser anterior à data inicial.";
    }
    if (precisaContas && !contasSelecionadas.length) {
      return "Selecione ao menos uma conta de resultado.";
    }
    return null;
  };

  const handleChangeEmpresasMulti = (_, value) => {
    const isSelecionarTodos = value?.some(
      (empresa) => empresa.id === "selecionarTodos"
    );
    if (isSelecionarTodos) {
      const isAllSelected = empresasSelecionadas.length === listaEmpresas.length;
      setEmpresasSelecionadas(isAllSelected ? [] : listaEmpresas);
      return;
    }
    setEmpresasSelecionadas(value ?? []);
  };

  const handleGerar = async () => {
    const err = validarComum();
    if (err) {
      setSnackErro({ open: true, msg: err });
      return;
    }

    const cnpjs = multiCnpj
      ? empresasSelecionadas.map((e) => String(e.id).replace(/\D/g, ""))
      : undefined;
    const cnpj = multiCnpj
      ? cnpjs[0]
      : String(empresaSelecionada.id).replace(/\D/g, "");
    const schema = multiCnpj
      ? undefined
      : `z_${empresaSelecionada.id}`;

    setLoading(true);
    try {
      const contas = precisaContas
        ? contasSelecionadas.map((c) => String(c.id))
        : undefined;
      const res = await processarRelatorio({
        idRelatorio,
        params: {
          dataInicial: dataInicial.trim(),
          dataFinal: dataFinal.trim(),
          cnpj,
          ...(cnpjs ? { cnpjs } : {}),
          ...(schema ? { schema } : {}),
          contas,
          notasSaida: notasSaida || "Cruzadas",
          prodId: Boolean(prodId),
        },
      });

      if (res?.sucesso) {
        const msg =
          res.mensagem || "Pedido aceito; o relatório será processado em fila.";
        if (onFinished) onFinished(true, msg);
        else onClose();
      } else {
        setSnackErro({
          open: true,
          msg: res?.mensagem || "Não foi possível enviar o pedido.",
        });
      }
    } catch (err) {
      const d = err?.response?.data;
      const msgFlat = Array.isArray(d?.message)
        ? d.message.join("; ")
        : typeof d?.message === "string"
          ? d.message
          : "";
      const msg =
        d?.mensagem ||
        msgFlat ||
        err?.message ||
        "Erro ao solicitar a exportação na fila.";
      setSnackErro({ open: true, msg });
    } finally {
      setLoading(false);
    }
  };

  const renderCampoPorFiltro = (filtro) => {
    const canon = canonicalFilaCsvToken(filtro?.variavel);
    const kind = tokenFieldKind(canon);
    switch (kind) {
      case "dataInicial":
      case "dataFinal":
      case "notasSaida":
      case "prodId":
        return null;
      case "contas":
        return (
          <Box key={`${canon}-contas`}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
                mb: 0.75,
              }}
            >
              <FormLabel component="legend" sx={{ mb: 0 }}>
                Contas de Resultado
              </FormLabel>
              <Button
                size="small"
                variant="text"
                onClick={() => setContasSelecionadas([...opcoesContas])}
                disabled={loadingContas || opcoesContas.length === 0}
              >
                Selecionar todas
              </Button>
            </Box>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={opcoesContas}
              value={contasSelecionadas}
              onChange={(_, v) => setContasSelecionadas(v)}
              getOptionLabel={(o) => o?.detalhes ?? ""}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              loading={loadingContas}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.detalhes}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  placeholder="Pesquisar contas de resultado…"
                />
              )}
            />
          </Box>
        );
      case "cnpj":
      case "schema":
        return null;
      default:
        return (
          <TextField
            key={`unk-${filtro.nome}-${filtro.variavel}`}
            label={filtro.nome || "Filtro"}
            value={filtro.variavel || ""}
            fullWidth
            disabled
            helperText="Token não mapeado para entrada; contacte suporte para ajustar o cadastro do filtro."
          />
        );
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Exportação assíncrona (fila). Preencha os campos e confirme.
        </Typography>

        <Autocomplete
          options={grupos}
          value={grupoSelecionado}
          onChange={(_, v) => setGrupoSelecionado(v)}
          getOptionLabel={(o) => o.detalhes}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          renderInput={(params) => (
            <TextField {...params} label="Grupo econômico" />
          )}
        />

        {listaEmpresas.length > 0 &&
          (multiCnpj ? (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={[
                { id: "selecionarTodos", detalhes: "Selecionar Todos" },
                ...listaEmpresas,
              ]}
              value={empresasSelecionadas}
              onChange={handleChangeEmpresasMulti}
              getOptionLabel={(o) => o.detalhes}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={
                        option.id === "selecionarTodos"
                          ? empresasSelecionadas.length === listaEmpresas.length
                          : selected
                      }
                    />
                    {option.detalhes}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Empresas (CNPJs)" />
              )}
            />
          ) : (
            <Autocomplete
              options={listaEmpresas}
              value={empresaSelecionada}
              onChange={(_, v) => setEmpresaSelecionada(v)}
              getOptionLabel={(o) => o.detalhes}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => (
                <TextField {...params} label="Empresa (CNPJ)" />
              )}
            />
          ))}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            label={filtroDataInicialMeta?.nome || "Data inicial"}
            type="date"
            value={dataInicial}
            onChange={(e) => setDataInicial(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label={filtroDataFinalMeta?.nome || "Data final"}
            type="date"
            value={dataFinal}
            onChange={(e) => setDataFinal(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>

        {filtrosCamposMeio.map((f) => renderCampoPorFiltro(f))}

        {(filtroNotasSaidaMeta || filtroProdIdMeta) && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: filtroNotasSaidaMeta && filtroProdIdMeta ? "1fr 1fr" : "1fr",
              },
              gap: 2,
              p: 1.75,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "action.selected" : "grey.50",
            }}
          >
            {filtroNotasSaidaMeta && (
              <FormControl fullWidth>
                <FormLabel component="legend">
                  {filtroNotasSaidaMeta.nome || "Notas de saída"}
                </FormLabel>
                <LinhaSwitchBinario
                  labelEsquerda="Cruzadas"
                  labelDireita="Todas"
                  checked={notasSaida === "Todas"}
                  onChange={(e) =>
                    setNotasSaida(e.target.checked ? "Todas" : "Cruzadas")
                  }
                  ariaLabel="Modo notas de saída"
                />
              </FormControl>
            )}
            {filtroProdIdMeta && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2">
                  {filtroProdIdMeta.nome || "Incluir ID produto"}
                </Typography>
                <Switch
                  checked={prodId}
                  onChange={(e) => setProdId(e.target.checked)}
                  sx={SX_SWITCH}
                />
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            mt: 1.25,
            pt: 1.25,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <BlueButton
            textValue={loading ? "Enviando…" : "Gerar na fila"}
            contentIcon={
              <SendIcon sx={{ fontSize: "1.1rem", color: "#fff" }} />
            }
            clickFunction={handleGerar}
            disabled={loading}
          />
        </Box>
      </Box>

      <MensagemErro
        open={snackErro.open}
        message={snackErro.msg}
        onClose={() => setSnackErro({ open: false, msg: "" })}
      />
    </>
  );
}
